import { useCallback } from "react";
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  AccountInfo,
  PopupRequest,
  SilentRequest,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { delay } from "@utils/delay";
import {
  HttpStatusError,
  type ProblemDetailsLike,
} from "@utils/httpStatusError";
import { useLoading } from "../context/LoadingContext";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface MsalRequest {
  scopes: string[];
  [key: string]: unknown;
}

/**
 * What fetchToken may do when silent renewal turns out to need the user:
 * - "popup-then-redirect" (default): open a popup; if the BROWSER BLOCKED it,
 *   fall back to one guarded full-page redirect. For user-initiated flows.
 * - "popup": popup only — a blocked popup is a thrown error, never a
 *   navigation. For anything running outside a user gesture (background
 *   events, MFE signals) where a surprise redirect would destroy open work.
 * - "none": silent only — interaction-required failures are thrown. For
 *   timers and housekeeping that must never show UI.
 */
export type InteractionPolicy = "popup-then-redirect" | "popup" | "none";

interface FetchTokenOptions {
  forceRefresh?: boolean;
  interaction?: InteractionPolicy;
}

/**
 * A non-2xx response, surfaced only to callers that opt in via
 * {@link ExecuteOptions.throwOnHttpError}. Defined in `@utils/httpStatusError`
 * and re-exported here so callers can import it alongside `execute`.
 */
export { HttpStatusError };

/**
 * The error body, when it is one. A gateway's HTML page or an empty body is not a failure to
 * report - it just means this did not come from our API, and the caller falls back to the generic
 * message.
 */
const parseProblem = (text: string): ProblemDetailsLike | undefined => {
  if (!text) return undefined;
  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as ProblemDetailsLike)
      : undefined;
  } catch {
    return undefined;
  }
};

export interface ExecuteOptions {
  /**
   * Throw {@link HttpStatusError} on a non-2xx response instead of parsing the
   * body. Needed by callers that must tell a transient failure from a genuinely
   * empty result — a background refetch has to retry the first and accept the
   * second.
   */
  throwOnHttpError?: boolean;
}

interface UseFetchWithMsalReturn {
  execute: <T = unknown>(
    method: HttpMethod,
    endpoint: string,
    data?: unknown,
    shouldShowLoader?: boolean,
    options?: ExecuteOptions,
  ) => Promise<T>;
}

/**
 * Silent-renewal failures that only an interactive flow can resolve.
 * InteractionRequiredAuthError is the designed signal, but the hidden-iframe
 * fallback inside acquireTokenSilent can ALSO die without it:
 * - monitor_window_timeout — the prompt=none iframe never posted its hash
 *   (blocked third-party cookies, or the redirectUri booting the whole SPA
 *   inside the iframe past the 10 s default timeout);
 * - block_iframe_reload — the app started re-rendering inside its own
 *   renewal iframe;
 * - hash_empty_error — the iframe returned with the response fragment
 *   stripped (code on the redirectUri page removed it), same root cause.
 * These used to be rethrown as generic failures, which callers presented as
 * "no access" after ~20 s of retries, when a popup would have fixed it.
 */
const silentFailureNeedsInteraction = (error: unknown): boolean =>
  error instanceof InteractionRequiredAuthError ||
  (error instanceof BrowserAuthError &&
    (error.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout ||
      error.errorCode === BrowserAuthErrorCodes.blockIframeReload ||
      error.errorCode === BrowserAuthErrorCodes.hashEmptyError));

/**
 * Popup failures that mean the BROWSER refused the window. Only these may
 * escalate to a redirect — user_cancelled is the user's explicit decision
 * and must never be answered with a full-page navigation that destroys
 * whatever they were protecting by closing the popup.
 */
const POPUP_BLOCKED_CODES = new Set<string>([
  BrowserAuthErrorCodes.popupWindowError,
  BrowserAuthErrorCodes.emptyWindowError,
]);

/**
 * One redirect-recovery attempt per browser session. fetchToken's redirect
 * fallback fires below every caller-level guard (GamContext's guard only
 * covers its own redirect call), so without this a boot-time failure where
 * AAD bounces straight back produces an unbounded app↔AAD ping-pong.
 * Cleared whenever acquisition succeeds again.
 */
export const MSAL_REDIRECT_GUARD = "msalRedirectRecoveryAttempted";

const clearRedirectRecoveryGuard = (): void => {
  try {
    sessionStorage.removeItem(MSAL_REDIRECT_GUARD);
  } catch {
    // sessionStorage unavailable (SSR/private mode) — the guard is then
    // never set either, so there is nothing to clear.
  }
};

// How long to wait for a FOREIGN in-flight interactive flow (one this module
// did not start — a redirect, or another MSAL surface) before giving up.
// Collisions between this module's own callers never wait here: they share
// the winner's promise via the single-flight below.
const INTERACTION_SETTLE_ATTEMPTS = 10;
const INTERACTION_SETTLE_DELAY_MS = 1000;

/**
 * Single-flight for the interactive fallback. Several independent callers
 * (GamContext, DynamicTable, tokenService, per-MFE hooks) can hit
 * interaction-required in the same tick; only the first may open a popup.
 * The rest await the same promise — however long the user takes with MFA —
 * instead of colliding into interaction_in_progress and polling on a timer.
 */
let inflightInteractive: Promise<string> | null = null;

const acquireInteractive = async (
  instance: IPublicClientApplication,
  silentRequest: SilentRequest,
  popupRequest: PopupRequest,
  interaction: Exclude<InteractionPolicy, "none">,
): Promise<string> => {
  if (inflightInteractive) {
    // Someone else is already recovering — share their outcome, then read
    // the shared cache. The retry keeps the CALLER's forceRefresh: a caller
    // that asked to bypass a server-rejected cached token must not be handed
    // that same token back.
    await inflightInteractive.catch(() => {
      /* the winner's failure surfaces through our own retry below */
    });
    const response = await instance.acquireTokenSilent(silentRequest);
    return response.accessToken;
  }

  const flow = (async () => {
    const response = await instance.acquireTokenPopup(popupRequest);
    return response.accessToken;
  })();
  inflightInteractive = flow;

  try {
    const token = await flow;
    clearRedirectRecoveryGuard();
    return token;
  } catch (popupError) {
    if (popupError instanceof BrowserAuthError) {
      if (
        popupError.errorCode === BrowserAuthErrorCodes.interactionInProgress
      ) {
        // An interaction this module does not own (a redirect flow, or MSAL
        // state from another surface). No promise to await — bounded wait,
        // then retry silently with the caller's original request.
        for (let i = 0; i < INTERACTION_SETTLE_ATTEMPTS; i++) {
          await delay(INTERACTION_SETTLE_DELAY_MS);
          try {
            const response = await instance.acquireTokenSilent(silentRequest);
            return response.accessToken;
          } catch {
            // Foreign flow not finished yet — keep waiting.
          }
        }
        throw popupError;
      }

      if (
        POPUP_BLOCKED_CODES.has(popupError.errorCode) &&
        interaction === "popup-then-redirect"
      ) {
        let guardSet = false;
        try {
          guardSet = Boolean(sessionStorage.getItem(MSAL_REDIRECT_GUARD));
          if (!guardSet) sessionStorage.setItem(MSAL_REDIRECT_GUARD, "1");
        } catch {
          guardSet = true; // no storage → no safe way to bound the loop
        }
        if (!guardSet) {
          // Popup blocked by the browser — a redirect always gets through.
          await instance.acquireTokenRedirect(popupRequest);
          return new Promise<string>(() => {}); // navigation takes over
        }
      }
    }
    // user_cancelled, guard-exhausted blocks, and everything else: the
    // caller decides. Escalating a deliberate cancel to a redirect would
    // destroy exactly the work the user closed the popup to protect.
    throw popupError;
  } finally {
    inflightInteractive = null;
  }
};

/**
 * Fetches an access token from MSAL, attempting silent acquisition first and
 * recovering interactively (per the caller's InteractionPolicy) when the
 * failure is one interaction can fix — including the hidden-iframe deaths
 * listed in silentFailureNeedsInteraction. Concurrent interactive recoveries
 * are single-flighted; see the notes on each constant above.
 */
export const fetchToken = async (
  instance: IPublicClientApplication,
  accounts: AccountInfo[],
  msalRequest: MsalRequest,
  options?: FetchTokenOptions,
): Promise<string> => {
  const interaction = options?.interaction ?? "popup-then-redirect";
  // Prefer the account MSAL considers active — accounts[0] can be a stale
  // account left behind in the localStorage cache by an earlier session,
  // whose dead refresh token then breaks every renewal until the user
  // clears site data by hand.
  const account = instance.getActiveAccount?.() ?? accounts[0];
  // Optional lightweight redirect target for the renewal iframe and popup.
  // The app-registration must list this URI before it is enabled (see
  // .env.example); unset, MSAL falls back to the configured redirectUri "/",
  // which boots the entire SPA inside the hidden iframe.
  const silentRedirectUri =
    process.env.NEXT_PUBLIC_MSAL_SILENT_REDIRECT_URI || undefined;
  const silentRequest: SilentRequest = {
    ...msalRequest,
    account,
    forceRefresh: options?.forceRefresh ?? false,
    ...(silentRedirectUri ? { redirectUri: silentRedirectUri } : {}),
  };

  try {
    const response = await instance.acquireTokenSilent(silentRequest);
    clearRedirectRecoveryGuard();
    return response.accessToken;
  } catch (error) {
    if (!silentFailureNeedsInteraction(error) || interaction === "none") {
      throw error;
    }
    const popupRequest: PopupRequest = {
      ...msalRequest,
      account,
      ...(silentRedirectUri ? { redirectUri: silentRedirectUri } : {}),
    };
    return acquireInteractive(
      instance,
      silentRequest,
      popupRequest,
      interaction,
    );
  }
};

/**
 * Custom hook to call a web API using bearer token obtained from MSAL
 * @param msalRequest - The MSAL request configuration containing scopes
 * @returns Object containing the execute function for making authenticated API calls
 */
const useFetchWithMsal = (msalRequest: MsalRequest): UseFetchWithMsalReturn => {
  const { instance, accounts } = useMsal();
  const { startLoading, stopLoading } = useLoading();

  const execute = useCallback(
    async <T = unknown,>(
      method: HttpMethod,
      endpoint: string,
      data: unknown = null,
      shouldShowLoader = false,
      options?: ExecuteOptions,
    ): Promise<T> => {
      try {
        if (shouldShowLoader) {
          startLoading();
        }

        const token = await fetchToken(instance, accounts, msalRequest);
        const headers = new Headers();
        headers.append("Authorization", `Bearer ${token}`);

        if (data) {
          headers.append("Content-Type", "application/json");
        }

        const fetchOptions: RequestInit = {
          method,
          headers,
          body: data ? JSON.stringify(data) : null,
        };

        const response = await fetch(endpoint, fetchOptions);

        // Read before deciding, so a failure can carry its body. The API answers a 5xx in a
        // service it depends on with a ProblemDetails naming that service, and that name is the
        // only way the page can tell the user which system is down - but the response is gone by
        // the time anyone catches the error, so it has to travel on the error itself.
        const text = await response.text();

        if (options?.throwOnHttpError && !response.ok) {
          throw new HttpStatusError(
            response.status,
            endpoint,
            parseProblem(text),
          );
        }

        const result = (text ? JSON.parse(text) : null) as T;

        if (shouldShowLoader) {
          stopLoading();
        }

        return result;
      } catch (error) {
        // Only dismiss the loader this call actually raised. Dismissing it
        // unconditionally let a failing background request take down a loader
        // that a *different*, still-running foreground request had put up —
        // which the reconnect refetch would hit on every failure.
        if (shouldShowLoader) {
          stopLoading();
        }
        throw error;
      }
    },
    [accounts, msalRequest, instance, startLoading, stopLoading],
  );

  return { execute };
};

export default useFetchWithMsal;
